/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/PanelManager" />
import { RepositoryTypes } from "Build/SourceProviders/Common";
import * as React from "react";
import { equals } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { ExternalConnection } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { ServiceEndpoint } from "Wit/Clients/ServiceEndpoint/Types/Contracts/ServiceEndpoint";
import { convertServiceEndpointToViewModel, isHosted } from "Wit/Settings/BoardsExternalIntegration/components/common/Utilities";
import {
    AddRepoManuallyPanel,
    AddRepoPanel,
    AuthenticationMethodPanel,
    CredentialsPanel,
    EnterpriseNewConnectionPanel,
    EnterprisePatPanel,
    NewConnectionPanel,
    OAuthPanel,
    Panel,
    PatPanel,
    RemoveRepoPanel
} from "Wit/Settings/BoardsExternalIntegration/components/panels";
import { IAuthenticationMethodPanelProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/authenticationMethod/AuthenticationMethodPanel.Props";
import { IPanelManagerProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/PanelManager.Props";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import {
    GitHubIntegrationCIActions,
    publishGitHubIntegrationTelemetryWithPageContext
} from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";
import {
    createOrUpdateConnection,
    getConnectionRecommendation,
    getProviderKey,
    isIE,
    removeConnection
} from "Wit/Settings/BoardsExternalIntegration/utils";
import { OrgSelectionPanel } from "./orgSelection/OrgSelectionPanel";

export interface IPanelManagerState {
    panel: Panel;
    connection?: ExternalConnection;
}

export class PanelManager extends VssComponent<IPanelManagerProps, IPanelManagerState, IVssComponentContext> {
    constructor(props: IPanelManagerProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            connection: this.props.connection,
            panel: this.props.panel
        };
    }

    public render(): JSX.Element {
        return <>{this._renderView()}</>;
    }

    private readonly _renderView = (): JSX.Element | undefined => {
        const { existingProviderRepos, existingServiceConnections, onCancel, onSave, isGitHubApp } = this.props;
        const { connection, panel } = this.state;
        const disableOAuth = isIE();

        switch (panel) {
            case Panel.NewConnection: {
                const props = {
                    disableOAuth,
                    onDismiss: onCancel,
                    showPanel: this._showPanel
                };

                return this._isHosted() ? <NewConnectionPanel {...props} /> : <EnterpriseNewConnectionPanel {...props} />;
            }

            case Panel.Pat: {
                const props = {
                    onDismiss: onCancel,
                    onConnectionCreated: this._onPatConnectionCreated,
                    existingServiceConnections: existingServiceConnections
                };

                return <PatPanel {...props} />;
            }

            case Panel.EnterprisePat: {
                const props = {
                    onDismiss: onCancel,
                    onConnectionCreated: this._onPatConnectionCreated,
                    existingServiceConnections: existingServiceConnections
                };

                return <EnterprisePatPanel {...props} />;
            }

            case Panel.EnterpriseAuthenticationMethod: {
                const props: IAuthenticationMethodPanelProps = {
                    disableOAuth,
                    onDismiss: onCancel,
                    showPanel: this._showPanel
                };

                return <AuthenticationMethodPanel {...props} />;
            }

            case Panel.EnterpriseCredentials: {
                const props = {
                    onDismiss: onCancel,
                    onConnectionCreated: this._onPatConnectionCreated,
                    existingServiceConnections: existingServiceConnections
                };
                return <CredentialsPanel {...props} />;
            }

            case Panel.EnterpriseOAuth: {
                return <OAuthPanel onDismiss={onCancel} />;
            }

            case Panel.AddRepo: {
                return (
                    connection && (
                        <AddRepoPanel
                            connection={connection}
                            reposFromSameProvider={existingProviderRepos && (existingProviderRepos[connection.providerKey] || [])}
                            onDismiss={onCancel}
                            onSave={onSave}
                            repositoryType={this.getRepositoryType(connection.providerKey)}
                            showPanel={this._showPanel}
                            isGitHubApp={isGitHubApp}
                            isAcquisitionFlow={this.props.isAcquisitionFlow}
                        />
                    )
                );
            }

            case Panel.AddRepoManually: {
                return (
                    connection && (
                        <AddRepoManuallyPanel
                            connection={connection}
                            repositoryType={this.getRepositoryType(connection.providerKey)}
                            onBackClick={this._changeViewTo(Panel.AddRepo)}
                            onDismiss={onCancel}
                            onSave={this.props.onSave}
                        />
                    )
                );
            }

            case Panel.RemoveRepo: {
                return (
                    connection && (
                        <RemoveRepoPanel
                            connection={connection}
                            repositoryType={this.getRepositoryType(connection.providerKey)}
                            onDismiss={onCancel}
                            onSave={onSave}
                        />
                    )
                );
            }

            case Panel.OrgSelection: {
                return (
                    connection && (
                        <OrgSelectionPanel
                            connection={connection}
                            onDismiss={onCancel}
                            reposFromSameProvider={(existingProviderRepos && existingProviderRepos[connection.providerKey]) || []}
                            onSave={onSave}
                            existingConnection={existingServiceConnections}
                        />
                    )
                );
            }
        }
    };

    private getRepositoryType(providerKey: string): string {
        return equals(providerKey, GitHubConnectionConstants.GitHubProviderKey, true) ? RepositoryTypes.GitHub : RepositoryTypes.GitHubEnterprise;
    }

    private readonly _isHosted = (): boolean => {
        return isHosted(this.context.pageContext);
    };

    private readonly _showPanel = (panel: Panel): void => {
        return this._changeViewTo(panel)();
    };

    private readonly _changeViewTo = (panel: Panel): (() => void) => {
        return () => {
            this.setState({
                panel
            });
        };
    };

    private readonly _onPatConnectionCreated = (serviceEndpoint: ServiceEndpoint) => {
        this.setState({
            connection: {
                providerKey: getProviderKey(serviceEndpoint.url),
                serviceEndpoint: convertServiceEndpointToViewModel(serviceEndpoint),
                externalGitRepos: [] as ExternalGitRepo[]
            } as ExternalConnection,
            panel: Panel.AddRepo
        });
    };
}

export function showPanel(
    pageContext: IVssPageContext,
    panel: Panel,
    existingConnections?: ExternalConnection[],
    connection?: ExternalConnection,
    isGitHubApp?: boolean,
    onPanelClosed?: () => void,
    onConnectionSaved?: (savedOrDeletedConnection: ExternalConnection, isDelete: boolean, partialProvisionError?: string) => void,
    onPanelCanceled?: () => void,
    isAcquisitionFlow?: boolean,
    showUnifiedExperience?: boolean
): void {
    let existingProviderRepos: { [providerKey: string]: ExternalGitRepo[] } | undefined = undefined;
    if (existingConnections) {
        existingProviderRepos = {};
        for (const connection of existingConnections) {
            const { providerKey, externalGitRepos } = connection;
            const isAppConnectionActive = !connection.statusMessage && connection.serviceEndpoint.isEndpointValid;

            // For in-active GitHub app connection, we skip the adding to existing provider repo
            // If the GitHub app is uninstalled and re-installed we dont want to filter the repo for new installation
            if (isAppConnectionActive) {
                if (!existingProviderRepos[providerKey]) {
                    existingProviderRepos[providerKey] = [];
                }
                existingProviderRepos[providerKey].push(...externalGitRepos);
            }
        }
    }

    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(closeCallout => {
        const closePanel = () => {
            closeCallout();
            onPanelClosed && onPanelClosed();
        };
        const onSave = async (connectionToSave: ExternalConnection, orgName?: string): Promise<ExternalConnection> => {
            let savedConnection = connectionToSave;
            let partialProvisionError = "";
            const isDelete = connectionToSave.externalGitRepos.length === 0;
            let isConnectionCreatedSuccessfully = false;

            // If the unified experience is turned on, we have to call the dp to check if the selected repos are part of installation
            // If not we need to redirect to GitHub to install the repo to the selected org
            // If the repos are already part of installation, we create the connection
            if (showUnifiedExperience) {
                const response = await getConnectionRecommendation(
                    pageContext,
                    connectionToSave,
                    orgName!,
                    (error: string) => (partialProvisionError = error)
                );

                if (response.externalConnectionProvisionResult && !response.externalConnectionProvisionResult.errorMessage) {
                    savedConnection = response.externalConnectionProvisionResult.provisionedConnection;
                    isConnectionCreatedSuccessfully = true;
                } else if (response.installAppUrl) {
                    publishGitHubIntegrationTelemetryWithPageContext(pageContext, GitHubIntegrationCIActions.ADMIN_INSTALLATION_REDIRECTION, {
                        repoCount: connectionToSave.externalGitRepos.length,
                        unifiedExperience: true
                    });
                    window.open(response.installAppUrl, "_self");
                } else {
                    throw new Error(response.errorMessage);
                }
            } else {
                if (isDelete) {
                    await removeConnection(pageContext, connectionToSave);
                } else {
                    savedConnection = await createOrUpdateConnection(
                        pageContext,
                        connectionToSave,
                        !!isGitHubApp,
                        (error: string) => (partialProvisionError = error)
                    );
                }

                isConnectionCreatedSuccessfully = true;
            }

            if (isConnectionCreatedSuccessfully) {
                // Close the panel
                closePanel();
                // Post panel operations
                onConnectionSaved && onConnectionSaved(savedConnection, isDelete, partialProvisionError);

                return savedConnection;
            } else {
                return connectionToSave;
            }
        };
        return (
            <PanelManager
                panel={panel}
                connection={connection}
                existingProviderRepos={existingProviderRepos}
                onSave={onSave}
                onCancel={() => {
                    closePanel();
                    onPanelCanceled && onPanelCanceled();
                }}
                existingServiceConnections={existingConnections || []}
                isGitHubApp={!!isGitHubApp}
                isAcquisitionFlow={isAcquisitionFlow}
            />
        );
    });
}
